/** Lead Advisor sub-steps in Project Onboarding (must match `LEAD_SUBSTEPS` keys in UI). */
export const LEAD_ONBOARDING_SUBSTEP_KEYS = [
  "questions",
  "risk-scale",
  "artefact-class",
  "kickoff",
  "review",
  "contracts",
];

export function normalizeLeadSubStep(value) {
  const s = String(value || "").trim();
  if (s === "artefact-templates") return "artefact-class";
  return LEAD_ONBOARDING_SUBSTEP_KEYS.includes(s) ? s : null;
}

/**
 * Resolve which tab to show from persisted wizard + questionnaire state.
 * Prefer explicit `activeLeadSubStep`; infer for projects saved before that field existed.
 */
export function inferLeadSubStepFromProfile(profile) {
  const raw = profile?.projectOnboardingWizard;
  const w = raw && typeof raw === "object" ? raw : {};
  const explicit = normalizeLeadSubStep(w.activeLeadSubStep);
  if (explicit) return explicit;

  const st = w.advisorArtefactsStatus;

  if (profile?.gapQuestionnaireDocument?.finalized) {
    return "contracts";
  }

  if (w.riskScaleComplete) {
    if (st === "awaiting_advisor" || st === "returned" || st === "submitted") {
      return "artefact-class";
    }
    if (st === "approved") {
      if (w.kickoffScheduled) {
        return "review";
      }
      return "kickoff";
    }
    if (st === undefined || st === null) {
      return "artefact-class";
    }
  }

  if (w.selectQuestions) return "risk-scale";
  return "questions";
}

export function mergeProjectOnboardingWizard(profile, patch) {
  const base = profile && typeof profile === "object" ? profile : {};
  const prev = base.projectOnboardingWizard && typeof base.projectOnboardingWizard === "object" ? base.projectOnboardingWizard : {};
  return { ...base, projectOnboardingWizard: { ...prev, ...patch } };
}

/** Raw onboarding review status from scoping profile (contract / MSA workflow). */
export function getStoredOnboardingReviewStatusFromProfile(profile) {
  if (!profile || typeof profile !== "object") return "Draft";
  const w = profile.projectOnboardingWizard;
  if (!w || typeof w !== "object") return "Draft";
  const s = w.onboardingReviewStatus;
  return typeof s === "string" && s ? s : "Draft";
}

/**
 * True when later phases (gap analysis, org setup) should unlock: contract published in wizard,
 * or gap analysis was finalized / pushed while onboardingReviewStatus lagged in JSON.
 */
export function isOnboardingWorkflowGateSatisfied(profile) {
  if (!profile || typeof profile !== "object") return false;
  if (getStoredOnboardingReviewStatusFromProfile(profile) === "Finalized") return true;
  const gw = profile.gapAnalysisWizard;
  if (!gw || typeof gw !== "object") return false;
  return gw.gapAnalysisStatus === "Finalized" || gw.pushedToDashboard === true;
}

/** Status for stepper / badges when gap progress implies onboarding is effectively complete. */
export function getOnboardingReviewStatusForUi(profile) {
  const stored = getStoredOnboardingReviewStatusFromProfile(profile);
  if (isOnboardingWorkflowGateSatisfied(profile) && stored !== "Finalized") {
    return "Finalized";
  }
  return stored;
}
